import { useState, useEffect } from "react";
import { Target, Calculator, Flame, Beef, Apple, Droplets } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  calculateNutritionTargets,
  getActivityLevelLabel,
  type NutritionGoalType,
  type TDEEParams,
  type NutritionTargets,
} from "@/utils/nutritionCalculator";

interface NutritionGoalsSetupProps {
  currentTargets?: {
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
    goalType?: NutritionGoalType;
  };
  userStats?: {
    weight?: number;
    height?: string;
    age?: number;
    sex?: string;
    activityLevel?: string;
  };
  onSave: (targets: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    goalType: NutritionGoalType;
  }) => Promise<void>;
  trigger?: React.ReactNode;
}

const GOAL_OPTIONS: { value: NutritionGoalType; label: string; description: string }[] = [
  { value: 'lose', label: 'Lose Weight', description: '~500 cal deficit' },
  { value: 'maintain', label: 'Maintain', description: 'Stay at current weight' },
  { value: 'gain', label: 'Build Muscle', description: '~300 cal surplus' },
];

const ACTIVITY_OPTIONS = [
  { value: 'sedentary', label: 'Sedentary' },
  { value: 'light', label: 'Light (1-3 days)' },
  { value: 'moderate', label: 'Moderate (3-5 days)' },
  { value: 'active', label: 'Active (6-7 days)' },
  { value: 'very_active', label: 'Very Active' },
];

export function NutritionGoalsSetup({
  currentTargets,
  userStats,
  onSave,
  trigger,
}: NutritionGoalsSetupProps) {
  const [open, setOpen] = useState(false);
  const [goalType, setGoalType] = useState<NutritionGoalType>(currentTargets?.goalType || 'maintain');
  const [activityLevel, setActivityLevel] = useState(userStats?.activityLevel || 'moderate');
  const [isManual, setIsManual] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Manual input values
  const [manualCalories, setManualCalories] = useState(currentTargets?.calories?.toString() || '');
  const [manualProtein, setManualProtein] = useState(currentTargets?.protein?.toString() || '');
  const [manualCarbs, setManualCarbs] = useState(currentTargets?.carbs?.toString() || '');
  const [manualFat, setManualFat] = useState(currentTargets?.fat?.toString() || '');

  // Calculated targets
  const [calculatedTargets, setCalculatedTargets] = useState<NutritionTargets | null>(null);

  // Check if we have enough data to auto-calculate
  const canAutoCalculate = userStats?.weight && userStats?.height && userStats?.age && userStats?.sex;

  useEffect(() => {
    if (canAutoCalculate && !isManual) {
      const params: TDEEParams = {
        weight: userStats.weight!,
        height: userStats.height!,
        age: userStats.age!,
        sex: userStats.sex as 'male' | 'female',
        activityLevel: activityLevel as TDEEParams['activityLevel'],
      };
      const targets = calculateNutritionTargets(params, goalType);
      setCalculatedTargets(targets);
    }
  }, [canAutoCalculate, userStats, goalType, activityLevel, isManual]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let targets: NutritionTargets;
      
      if (isManual) {
        const calories = parseInt(manualCalories);
        const protein = parseInt(manualProtein);
        const carbs = parseInt(manualCarbs);
        const fat = parseInt(manualFat);
        
        if (!calories || calories < 500) {
          toast.error('Please enter a valid calorie target (min 500)');
          setIsSaving(false);
          return;
        }
        
        targets = {
          calories,
          protein: protein || Math.round(calories * 0.25 / 4), // default 25% protein
          carbs: carbs || Math.round(calories * 0.45 / 4), // default 45% carbs
          fat: fat || Math.round(calories * 0.30 / 9), // default 30% fat
        };
      } else if (calculatedTargets) {
        targets = calculatedTargets;
      } else {
        toast.error('Unable to calculate targets. Please enter manually.');
        setIsSaving(false);
        return;
      }

      await onSave({
        ...targets,
        goalType,
      });

      toast.success('Nutrition goals saved!');
      setOpen(false);
    } catch (error) {
      toast.error('Failed to save goals');
    } finally {
      setIsSaving(false);
    }
  };

  const displayTargets = isManual 
    ? {
        calories: parseInt(manualCalories) || 0,
        protein: parseInt(manualProtein) || 0,
        carbs: parseInt(manualCarbs) || 0,
        fat: parseInt(manualFat) || 0,
      }
    : calculatedTargets || currentTargets;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <Target className="w-4 h-4" />
            Set Goals
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Nutrition Goals
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Goal Type Selection */}
          <div className="space-y-2">
            <Label className="text-sm">What's your goal?</Label>
            <div className="grid grid-cols-3 gap-2">
              {GOAL_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setGoalType(option.value)}
                  className={cn(
                    "flex flex-col items-center p-3 rounded-xl border transition-all text-center",
                    goalType === option.value
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <span className="text-sm font-medium">{option.label}</span>
                  <span className={cn(
                    "text-[10px] mt-1",
                    goalType === option.value ? "text-primary-foreground/80" : "text-muted-foreground"
                  )}>
                    {option.description}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Activity Level */}
          {canAutoCalculate && !isManual && (
            <div className="space-y-2">
              <Label className="text-sm">Activity Level</Label>
              <Select value={activityLevel} onValueChange={setActivityLevel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTIVITY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Toggle between auto and manual */}
          <div className="flex items-center justify-between py-2 border-t border-border/50">
            <span className="text-sm text-muted-foreground">
              {canAutoCalculate ? 'Auto-calculated from your profile' : 'Enter targets manually'}
            </span>
            {canAutoCalculate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsManual(!isManual)}
                className="text-xs"
              >
                {isManual ? (
                  <>
                    <Calculator className="w-3 h-3 mr-1" />
                    Use Auto
                  </>
                ) : (
                  'Customize'
                )}
              </Button>
            )}
          </div>

          {/* Manual inputs or calculated display */}
          {isManual || !canAutoCalculate ? (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="calories" className="text-xs flex items-center gap-1">
                  <Flame className="w-3 h-3 text-orange-500" /> Daily Calories *
                </Label>
                <Input
                  id="calories"
                  type="number"
                  placeholder="e.g., 2000"
                  value={manualCalories}
                  onChange={(e) => setManualCalories(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="protein" className="text-xs flex items-center gap-1">
                    <Beef className="w-3 h-3 text-red-500" /> Protein (g)
                  </Label>
                  <Input
                    id="protein"
                    type="number"
                    placeholder="150"
                    value={manualProtein}
                    onChange={(e) => setManualProtein(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="carbs" className="text-xs flex items-center gap-1">
                    <Apple className="w-3 h-3 text-yellow-500" /> Carbs (g)
                  </Label>
                  <Input
                    id="carbs"
                    type="number"
                    placeholder="200"
                    value={manualCarbs}
                    onChange={(e) => setManualCarbs(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="fat" className="text-xs flex items-center gap-1">
                    <Droplets className="w-3 h-3 text-blue-500" /> Fat (g)
                  </Label>
                  <Input
                    id="fat"
                    type="number"
                    placeholder="65"
                    value={manualFat}
                    onChange={(e) => setManualFat(e.target.value)}
                  />
                </div>
              </div>
            </div>
          ) : (
            /* Auto-calculated targets display */
            displayTargets && (
              <div className="bg-secondary/30 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Flame className="w-5 h-5 text-orange-500" />
                    <span className="text-sm">Daily Calories</span>
                  </div>
                  <span className="text-xl font-display font-bold text-primary">
                    {displayTargets.calories?.toLocaleString()}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border/50">
                  <div className="text-center">
                    <Beef className="w-4 h-4 mx-auto text-red-500" />
                    <p className="text-lg font-bold">{displayTargets.protein}g</p>
                    <p className="text-xs text-muted-foreground">Protein</p>
                  </div>
                  <div className="text-center">
                    <Apple className="w-4 h-4 mx-auto text-yellow-500" />
                    <p className="text-lg font-bold">{displayTargets.carbs}g</p>
                    <p className="text-xs text-muted-foreground">Carbs</p>
                  </div>
                  <div className="text-center">
                    <Droplets className="w-4 h-4 mx-auto text-blue-500" />
                    <p className="text-lg font-bold">{displayTargets.fat}g</p>
                    <p className="text-xs text-muted-foreground">Fat</p>
                  </div>
                </div>
              </div>
            )
          )}

          <Button 
            onClick={handleSave}
            disabled={isSaving}
            className="w-full"
          >
            {isSaving ? 'Saving...' : 'Save Goals'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
